import { isEmpty as _isEmpty } from 'lodash-es'
import { useBranchProducts } from './useBranchProducts'
import { usePlatformStore } from '@/store-pinia/platform'
import utils from '@/utils'
import { ProductSalesMode } from '@/view-models/product'

export function useProductPlatformInfo(_product = ref({})) {
  const product = computed(() => unref(_product))
  const platformStore = usePlatformStore()
  /** 目前只有 FunBook v2 要看 is_paid_onsite */
  const isPaidOnsite = computed(() => !!(platformStore.isFunBookV2 && product.value?.is_paid_onsite))
  const isBookSeatOnly = computed(() => !!product.value?.is_book_seat_only)

  return {
    isPaidOnsite,
    isBookSeatOnly,
  }
}

export function usePlatformProductDetail() {
  const { $store: store } = useNuxtApp()

  const productFromStore = computed(() => store.state.product.productInfo)

  const { isBookSeatOnly, isPaidOnsite } = useProductPlatformInfo(productFromStore)
  const product = computed(() => ({
    ...productFromStore.value,
    isBookSeatOnly: isBookSeatOnly.value,
    isPaidOnsite: isPaidOnsite.value,
  }))

  return {
    product,
  }
}

/**
 * 方案資訊：FunBook 1.0, 2.0 的方案欄位並不相同，這裡暫時不定義所有欄位
 * @typedef ProductInfo
 * @prop {number} product_id
 * @prop {number} pid
 * @prop {string} product_name
 * @prop {0 | 1} onboard
 * @prop {boolean} is_cust_num
 * @prop {0 | 1} is_crossday
 * @prop {0 | 1} is_book_seat_only
 * @prop {0 | 1} showDuration
 * @prop {number | string} duration
 * @prop {boolean} is_elite
 */

/**
 * 選擇方案
 * ! for component only
 * @param {import('vue').Ref<ProductInfo[]>} products
 * @returns
 */
export function useProductSelect(products = ref([]), { bookingQueries } = {}) {
  const headcount = computed(() => unref(bookingQueries?.headcount) || 0)
  const { $store: store, $auth, $i18n } = useNuxtApp()
  const { isFunBookV2 } = storeToRefs(usePlatformStore())

  const { branchProducts, sortedBranchProducts } = useBranchProducts(products)
  const userInfo = computed(() => $auth.user)

  /** @type {import('vue').Ref<ProductInfo>} */
  const selectedProduct = ref({})

  /** 過度期 flag: FunBook 1.0、BookingEngine 未來都會整併至 FunBook 2.0 */
  const isBookingEngine = computed(() => store.getters.isBookingEngine)
  const isFunBookV1 = computed(() => store.state.branch.branch.funbook_ver === 1)

  const productID = computed(() => selectedProduct.value?.product_id || '')
  const productText = computed(() => selectedProduct.value?.product_name || '')

  /** @type {import('vue').Ref<ProductInfo[]>} */
  const productUiItems = computed(() =>
    sortedBranchProducts.value.map(product => {
      const isDisabledByElite = product.is_elite && userInfo.value?.is_elite === false
      const optionDisabledText = generateDisabledText(product, { isDisabledByElite, headcount: headcount.value })

      return {
        ...product,
        paymentLabel: getPaymentLabel(product),
        showImage: product.onboard && product.cover_image,
        showDescription: !!product.description,
        showVipInfo: product.vip_reward && !isFunBookV2.value,
        showBookSeatOnly: product.is_book_seat_only && product.onboard,
        isCampingSalesMode: product.sales_mode === ProductSalesMode.Camping,
        /** 顯示服務時間 */
        showDuration: (isFunBookV2.value || isFunBookV1.value) && !product.is_cust_num && product.duration && product.duration <= 360,
        /** 服務時間 */
        duration: product.duration ? `${product.duration} ${$i18n.t('time_minute')}` : '',
        minPrice: formatThousandsSeparators(isBookingEngine.value ? product.min_price : product.discount),
        rrp: formatThousandsSeparators(product.rrp),
        coverImage: utils.getMediaCdnUrl(product.cover_image),
        isDisabledByElite,
        isOptionDisabled: !product.onboard || isDisabledByElite,
        optionDisabledText,
      }
    })
  )

  const hasAvailableProduct = computed(() => sortedBranchProducts.value.some(product => product.onboard))

  /** 唯一的方案（不管 onboard 狀態，只看方案列表僅一個方案） */
  const uniqueProduct = computed(() => (productUiItems.value.length === 1 ? productUiItems.value[0] : {}))

  function formatThousandsSeparators(number) {
    const price = parseFloat(number)
    return price ? price.toLocaleString() : number
  }

  function setAutoSelectProduct() {
    if (!productUiItems.value.length) {
      return false
    }
    // only one product
    if (!_isEmpty(uniqueProduct.value) && !uniqueProduct.value.isOptionDisabled) {
      selectedProduct.value = uniqueProduct.value
      return true
    }
    // select `book seat only` product first
    const firstBookSeatOnlyProduct = productUiItems.value.find(product => !product.isOptionDisabled && product.is_book_seat_only)

    if (firstBookSeatOnlyProduct && !isFunBookV2.value) {
      selectedProduct.value = firstBookSeatOnlyProduct
      return true
    }

    selectedProduct.value =
      productUiItems.value.find(product => !product.isOptionDisabled && selectedProduct.value.product_id === product.product_id) || {}

    return true
  }

  function generateDisabledText(product, { isDisabledByElite, headcount }) {
    // 可預訂 但可能有 elite member 限制
    if (product.onboard) {
      return isDisabledByElite ? $i18n.t('elite_member_only') : ''
    }

    // 不可預訂時的判斷
    if (product.onboard_cust_num === 0) {
      return $i18n.t('all_sold_out')
    }
    if (headcount < product.onboard_cust_num) {
      return $i18n.t('cust_num_lower_limit', { cust_num: product.onboard_cust_num })
    }
    if (headcount > product.onboard_cust_num) {
      return $i18n.t('cust_num_upper_limit', { cust_num: product.onboard_cust_num })
    }

    return ''
  }

  function getPaymentLabel(product) {
    // only funbook v2 has payment label
    if (isFunBookV2.value) {
      return product?.is_paid_onsite ? $i18n.t('funbook_pay_when_arrived') : $i18n.t('funbook_pay_online')
    }
    return ''
  }

  return {
    selectedProduct,
    branchProducts,
    productUiItems,
    productID,
    hasAvailableProduct,
    productText,
    setAutoSelectProduct,
  }
}
